/**
 * xAI Grok Realtime WebSocket transport.
 *
 * Unlike OpenAI/Azure (browser WebRTC via an SDP POST to /calls), xAI's realtime
 * is a WebSocket: `wss://api.x.ai/v1/realtime?model=…` with the ephemeral token in
 * the WS subprotocol (browsers can't set WS headers). It's OpenAI-Realtime
 * COMPATIBLE with minor event-name differences.
 *
 * Modeled on xai-org/xai-cookbook voice-examples/agent/web (the authoritative
 * browser client): NATIVE-sample-rate PCM16 both ways, session.update sent after
 * `conversation.created`, and the greeting kicked after `session.updated` via a
 * user text item + response.create. Output audio arrives as `…output_audio.delta`
 * with the base64 in the `delta` field (NOT `audio`).
 *
 * This transport does what WebRTC did for free: capture the mic as PCM16, stream
 * it as `input_audio_buffer.append`, and play the returned PCM16 deltas. It
 * exposes the same {callId, send, close} channel the hook consumes. (A copy of the habits
 * transport — the fleet keeps one per app until @owlcat/realtime grows a browser side.)
 */
import type { RealtimeChannel } from "./geminiLive";

const CHUNK_MS = 100; // send mic in ~100ms chunks (cookbook default)

export interface WsSessionConfig {
  instructions: string;
  tools?: unknown[];
  voice: string;
  model: string;
  /** Prompt (in the child's language) that kicks the pet's opening line. */
  greeting: string;
  /** BCP-47 language of the speaker — the input transcription hint (default ru) */
  language?: string;
}

export interface ConnectWsOptions {
  ephemeralKey: string;
  wsUrl: string;
  /** "subprotocol" = xAI direct (the ephemeral rides in the WS subprotocol); "ticket" = the worker
   *  RELAY (`?ticket=`; the relay owns session.update and meters — production only mints this) */
  wsAuthParam?: "subprotocol" | "ticket";
  micStream: MediaStream;
  session: WsSessionConfig;
  onEvent: (event: Record<string, unknown>) => void;
  onError: (err: Error) => void;
  onOpen?: () => void;
  /** Fired once after `silenceMs` of child silence (pet not speaking) — the hook
   *  tears the socket down to stop burning minutes and waits for voice to resume. */
  onIdle?: () => void;
  /** Silence before onIdle fires (ms). 0 disables idle auto-close. */
  silenceMs?: number;
}

const VAD_RMS = 0.015; // mic RMS above this = the child is talking

/** Float32 [-1,1] → base64 little-endian PCM16. */
function floatToB64Pcm16(input: Float32Array): string {
  const pcm = new Int16Array(input.length);
  for (let i = 0; i < input.length; i++) {
    const s = Math.max(-1, Math.min(1, input[i]));
    pcm[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  const bytes = new Uint8Array(pcm.buffer);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

/** base64 PCM16 → Float32Array [-1,1]. */
function b64Pcm16ToFloat(b64: string): Float32Array {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const pcm = new Int16Array(bytes.buffer);
  const out = new Float32Array(pcm.length);
  for (let i = 0; i < pcm.length; i++) out[i] = pcm[i] / (pcm[i] < 0 ? 0x8000 : 0x7fff);
  return out;
}

export async function connectRealtimeWS(opts: ConnectWsOptions): Promise<RealtimeChannel> {
  const { ephemeralKey, wsUrl, micStream, session, onEvent, onError, onOpen, onIdle } = opts;
  const silenceMs = opts.silenceMs ?? 0;

  // ONE AudioContext at the browser's NATIVE rate (capture + playback), like the
  // cookbook — no forced resampling, and we tell the server that same rate.
  const ctx = new AudioContext();
  const rate = ctx.sampleRate;
  const source = ctx.createMediaStreamSource(micStream);
  const proc = ctx.createScriptProcessor(4096, 1, 1);

  // GAPLESS playback scheduled on the context clock (see geminiLive.ts): chaining chunks on
  // onended left ms-gaps that clicked at every boundary; a small lead absorbs jitter.
  const LEAD_S = 0.12;
  const sources = new Set<AudioBufferSourceNode>();
  // an analyser on the playback path: the creature's mouth opens with the agent's loudness
  const analyser = ctx.createAnalyser(); analyser.fftSize = 256; analyser.connect(ctx.destination);
  const aData = new Uint8Array(analyser.frequencyBinCount);
  const level = () => {
    if (!playing) return 0;
    analyser.getByteTimeDomainData(aData);
    let sum = 0; for (let i = 0; i < aData.length; i++) { const v = (aData[i] - 128) / 128; sum += v * v; }
    return Math.min(1, Math.sqrt(sum / aData.length) * 3.5);
  };
  let nextAt = 0;
  let playing = false;
  const enqueue = (f32: Float32Array) => {
    if (ctx.state === "suspended") void ctx.resume().catch(() => {});
    const buf = ctx.createBuffer(1, f32.length, rate);
    buf.getChannelData(0).set(f32);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(analyser);
    const now = ctx.currentTime;
    if (nextAt < now + 0.005) nextAt = now + LEAD_S;
    src.start(nextAt);
    nextAt += buf.duration;
    playing = true;
    sources.add(src);
    src.onended = () => { sources.delete(src); if (sources.size === 0) playing = false; };
  };
  const stopPlayback = () => {
    // Barge-in: drop everything scheduled.
    for (const s of sources) { try { s.stop(); s.disconnect(); } catch { /* already stopped */ } }
    sources.clear();
    nextAt = 0;
    playing = false;
  };

  const send = (event: unknown) => {
    if (ws.readyState === WebSocket.OPEN) {
      try {
        ws.send(JSON.stringify(event));
      } catch {
        /* closing mid-send */
      }
    }
  };

  let configured = false; // gate mic + greeting on session.updated

  // Browsers can't set WS headers → xAI direct: the ephemeral rides in the subprotocol; the worker
  // relay takes its one-use ticket as a query parameter instead.
  const ws = opts.wsAuthParam === "ticket"
    ? new WebSocket(`${wsUrl}${wsUrl.includes("?") ? "&" : "?"}ticket=${encodeURIComponent(ephemeralKey)}`)
    : new WebSocket(wsUrl, [`xai-client-secret.${ephemeralKey}`]);
  ws.binaryType = "arraybuffer";

  let closed = false;
  const close = () => {
    if (closed) return;
    closed = true;
    stopPlayback();
    try {
      proc.disconnect();
      source.disconnect();
    } catch {
      /* ignore */
    }
    // NOTE: the mic stream is OWNED BY THE HOOK (it survives idle-close so the local
    // VAD can wait for the child's voice and reconnect). We never stop its tracks
    // here — the hook stops them on a full stop().
    void ctx.close().catch(() => {});
    try {
      ws.close();
    } catch {
      /* already closing */
    }
  };

  // Idle auto-close bookkeeping: last time the child was heard, whether the pet is
  // mid-response (never idle-close while it's talking), and a one-shot latch.
  let lastVoiceMs = Date.now();
  let responseActive = false;
  let idleFired = false;

  ws.onopen = () => {
    try {
      if (ctx.state === "suspended") void ctx.resume().catch(() => {});
      // Session config (xAI-shaped) at the NEGOTIATED native rate. `transcription`
      // on the input turns on the CHILD's speech-to-text.
      send({
        type: "session.update",
        session: {
          type: "realtime",
          model: session.model,
          voice: session.voice,
          instructions: session.instructions,
          ...(session.tools && session.tools.length ? { tools: session.tools, tool_choice: "auto" } : {}),
          turn_detection: { type: "server_vad" },
          audio: {
            input: {
              format: { type: "audio/pcm", rate },
              transport: "json",
              transcription: { language_hint: session.language ?? "ru" },
            },
            output: { format: { type: "audio/pcm", rate }, transport: "json" },
          },
        },
      });
      // Stream the mic in ~100ms chunks (dropped until the session is configured).
      let buf: Float32Array[] = [];
      let total = 0;
      const chunkSamples = Math.round((rate * CHUNK_MS) / 1000);
      proc.onaudioprocess = (e) => {
        if (!configured || ws.readyState !== WebSocket.OPEN) return;
        const input = e.inputBuffer.getChannelData(0);
        // Local VAD: RMS over the frame. Above threshold = the child is talking →
        // remember the time. Silence for silenceMs (and the pet isn't talking) →
        // fire onIdle ONCE so the hook can drop the session and save minutes.
        let sum = 0;
        for (let i = 0; i < input.length; i++) sum += input[i] * input[i];
        const rms = Math.sqrt(sum / input.length);
        const now = Date.now();
        if (rms > VAD_RMS) lastVoiceMs = now;
        if (
          silenceMs > 0 &&
          !idleFired &&
          !responseActive &&
          !playing &&
          now - lastVoiceMs > silenceMs
        ) {
          idleFired = true;
          try {
            onIdle?.();
          } catch {
            /* hook handles teardown */
          }
        }
        buf.push(new Float32Array(input));
        total += e.inputBuffer.length;
        while (total >= chunkSamples) {
          const chunk = new Float32Array(chunkSamples);
          let off = 0;
          while (off < chunkSamples && buf.length) {
            const b = buf[0];
            const need = chunkSamples - off;
            if (b.length <= need) {
              chunk.set(b, off);
              off += b.length;
              total -= b.length;
              buf.shift();
            } else {
              chunk.set(b.subarray(0, need), off);
              buf[0] = b.subarray(need);
              off += need;
              total -= need;
            }
          }
          send({ type: "input_audio_buffer.append", audio: floatToB64Pcm16(chunk) });
        }
      };
      source.connect(proc);
      proc.connect(ctx.destination); // ScriptProcessor needs a sink to pump
      onOpen?.();
    } catch (err) {
      onError(err instanceof Error ? err : new Error(`ws onopen failed: ${String(err)}`));
    }
  };

  ws.onmessage = (e) => {
    if (e.data instanceof ArrayBuffer) return; // json transport; ignore stray binary
    let msg: Record<string, unknown>;
    try {
      msg = JSON.parse(e.data as string);
    } catch {
      return;
    }
    const type = msg.type as string;
    // Track whether the pet is mid-response (so we never idle-close while it talks;
    // and the silence clock restarts from the END of the pet's turn).
    if (type === "response.created") responseActive = true;
    else if (type === "response.done") {
      responseActive = false;
      lastVoiceMs = Date.now();
    }
    // Kick the greeting once the session is applied (cookbook sequence).
    if (type === "session.updated" && !configured) {
      configured = true;
      send({
        type: "conversation.item.create",
        item: { type: "message", role: "user", content: [{ type: "input_text", text: session.greeting }] },
      });
      send({ type: "response.create" });
    }
    // Play bot audio — xAI puts the base64 in `delta` (accept `audio` too).
    else if (type === "response.output_audio.delta" || type === "response.audio.delta") {
      const b64 = (msg.delta as string) ?? (msg.audio as string);
      if (b64) enqueue(b64Pcm16ToFloat(b64));
    }
    // Barge-in: child starts talking → stop the pet mid-sentence.
    else if (type === "input_audio_buffer.speech_started") {
      stopPlayback();
    }
    // Surface provider errors (why a session dies) instead of silently closing.
    else if (type === "error") {
      console.error("[pet-voice] xai error event:", JSON.stringify(msg).slice(0, 500));
    }
    onEvent(msg);
  };

  ws.onerror = () => onError(new Error("xai realtime websocket error"));
  ws.onclose = (ev) => {
    if (!closed && ev.code !== 1000) onError(new Error(`xai realtime ws closed ${ev.code} ${ev.reason || ""}`.trim()));
  };

  return { callId: null, send, close, level };
}
